import React, { useEffect, useState, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import {
  Clock,
  Users,
  Settings,
  LogOut,
  Globe,
  Truck,
  DollarSign,
  Activity,
} from "react-feather";
import { BiRestaurant, BiFoodMenu } from "react-icons/bi";
import { MdTableRestaurant } from "react-icons/md";
import { FiHome, FiShoppingCart } from "react-icons/fi";
import { all_routes } from "../../Router/all_routes";
// import { base_api_url } from "../../environment";
import { GiHotMeal } from "react-icons/gi";
import { QrCode, UserCheck } from "lucide-react";

const ModernSidebar = ({ isCollapsed, isMobile, isMobileMenuOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [menuItems, setMenuItems] = useState([]);
  const menuStructure = useMemo(() => [
    {
      id: "etablissement",
      title: "ACCUEIL",
      items: [
        {
          label: "Dashboard",
          link: all_routes.accueil,
          icon: <FiHome size={20} />,
        },
        {
          label: "Restaurants",
          link: "/restaurants",
          icon: <BiRestaurant size={20} />,
        },
        {
          label: "Équipes",
          link: "/resto-users",
          icon: <Users size={20} />,
        },
      ],
    },
    {
      id: "cartes",
      title: "Cartes & Menus",
      items: [
        {
          label: "Catalogue",
          link: "/articles-new",
          icon: <GiHotMeal size={20} />,
        },
        {
          label: "Menus",
          link: "/menus",
          icon: <BiFoodMenu size={20} />,
        },
      ],
    },
    {
      id: "qrcodes",
      title: "QR CODES",
      items: [
        {
          label: "QR Salle",
          link: "/tables-new",
          icon: <MdTableRestaurant size={20} />,
        },
        {
          label: "Marketplace",
          link: "/marketplace",
          icon: <Globe size={20} />,
        },
      ],
    },
    {
      id: "operations",
      title: "OPÉRATIONS",
      items: [
        {
          label: "Commandes",
          link: "/commands-new",
          icon: <FiShoppingCart size={20} />,
        },
        {
          label: "Achats",
          link: all_routes.achats,
          icon: <DollarSign size={20} />,
        },
      ],
    },
    {
      id: "pilotage",
      title: "PILOTAGE",
      items: [
        {
          label: "Inventaire & Stocks",
          link: all_routes.ingredients,
          icon: <Clock size={20} />,
        },
        {
          label: "Client",
          link: all_routes.clientpreview,
          icon: <UserCheck size={20} />,
        },
        {
          label: "Fournisseurs",
          link: all_routes.fournisseurs,
          icon: <Truck size={20} />,
        },
        {
          label: "KPIs",
          link: all_routes.mesKpi,
          icon: <Activity size={20} />,
        },
      ],
    },
    {
      id: "autres",
      title: "AUTRES",
      items: [],
    },
  ], []);

  // Bottom menu items
  const bottomMenuItems = [
    {
      label: "Paramètres",
      link: all_routes.settings,
      icon: <Settings size={20} />,
    },
    {
      label: "Déconnexion",
      link: "/signin",
      icon: <LogOut size={20} />,
      action: "logout",
    },
  ];

  const [currentRestoId, setCurrentRestoId] = useState(
    JSON.parse(localStorage.getItem("user"))?.restoId
  );

  useEffect(() => {
    const handleRestaurantChange = () => {
      const user = JSON.parse(localStorage.getItem("user"));
      setCurrentRestoId(user?.restoId);
    };

    window.addEventListener("storage", handleRestaurantChange);
    window.addEventListener("restaurantChanged", handleRestaurantChange);

    return () => {
      window.removeEventListener("storage", handleRestaurantChange);
      window.removeEventListener("restaurantChanged", handleRestaurantChange);
    };
  }, []);

  useEffect(() => {
    const role = JSON.parse(localStorage.getItem("user"))?.role;

    // Filter menu based on role and restaurant selection
    const filteredStructure = menuStructure.map((section) => {
      let filteredItems = [...section.items];

      // Role based filtering
      if (role === 3) {
        filteredItems = filteredItems.filter((item) =>
          ["Commandes"].includes(item.label)
        );
      } else if (role === 2) {
        filteredItems = filteredItems.filter((item) =>
          !["Équipes", "Mes restaurants"].includes(item.label)
        );
      }

      // Restaurant existence filtering
      if (!currentRestoId) {
        // Hide Inventory and Purchases if no restaurant is selected
        filteredItems = filteredItems.filter(item =>
          !["Inventaire & Stocks", "Achats"].includes(item.label)
        );
      }

      return filteredItems.length > 0 ? { ...section, items: filteredItems } : null;
    }).filter(Boolean);

    setMenuItems(filteredStructure);
  }, [menuStructure, currentRestoId]);

  // Listen to restaurant changes in localStorage
  // useEffect(() => {
  //   const updateRestaurantInfo = () => {
  //     const restaurant = JSON.parse(localStorage.getItem("restaurant") || "{}");
  //     const name = restaurant?.restaurant?.nom || "QR100";
  //     const logo = restaurant?.restaurant?.logo || "/assets/img/qr100/logo.png";
  //     // setRestaurantInfo({ name, logo });
  //   };

  //   // Initial load
  //   updateRestaurantInfo();

  //   // Listen for storage changes
  //   window.addEventListener("storage", updateRestaurantInfo);

  //   // Custom event for same-tab updates
  //   window.addEventListener("restaurantChanged", updateRestaurantInfo);

  //   return () => {
  //     window.removeEventListener("storage", updateRestaurantInfo);
  //     window.removeEventListener("restaurantChanged", updateRestaurantInfo);
  //   };
  // }, []);

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("restaurant");
    navigate(all_routes.signin);
  };

  const handleMenuItemClick = (item, event) => {
    if (item.label === "Déconnexion" || item.action === "logout") {
      event.preventDefault();
      handleLogout();
    }
  };

  const isActiveLink = (link) => {
    return location.pathname === link || location.pathname.startsWith(link + "/");
  };

  // Sur mobile, on force l'état "collapsed" visuellement pour les éléments internes
  const effectivelyCollapsed = isMobile || isCollapsed;

  // Déterminer les classes CSS selon le mode (mobile ou desktop)
  const sidebarClasses = () => {
    if (isMobile) {
      // En mobile : collapsed par défaut, mais peut être ouvert
      return `modern-sidebar modern-sidebar--mobile ${isMobileMenuOpen ? 'modern-sidebar--mobile-open' : 'modern-sidebar--mobile-closed'}`;
    }
    // En desktop : comportement normal collapsed/expanded
    return `modern-sidebar ${isCollapsed ? 'modern-sidebar--collapsed' : 'modern-sidebar--expanded'}`;
  };

  return (
    <aside className={sidebarClasses()}>
      {/* Logo QRNYAMA */}
      <div className={`modern-sidebar__header ${effectivelyCollapsed ? 'modern-sidebar__header--collapsed' : ''}`}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: effectivelyCollapsed ? "center" : "flex-start",
            gap: effectivelyCollapsed ? "0" : "0.75rem",
            marginBottom: "8px",
            width: "100%",
          }}
        >
          <div className="signin-logo-icon" style={{ margin: effectivelyCollapsed ? "0 auto" : "0" }}>
            <QrCode />
          </div>
          {!effectivelyCollapsed && (
            <span
              className="text-black"
              style={{
                fontSize: "1.5rem",
                fontWeight: 700,
                letterSpacing: "-0.025em",
                color: "white",
                textTransform: "uppercase",
              }}
            >
              QR<span className="signin-logo-text-accent">NYAMA</span>
            </span>
          )}
        </div>
      </div>


      {/* Restaurant Header */}
      {/* <div className={`modern-sidebar__header ${isCollapsed ? 'modern-sidebar__header--collapsed' : ''}`}>
        <img
          src={restaurantInfo.logo?.startsWith('qr100-') ? `${base_api_url}doc/recuperer_fichier/${restaurantInfo.logo}` : restaurantInfo.logo}
          alt={restaurantInfo.name}
          className={`modern-sidebar__logo ${isCollapsed ? 'modern-sidebar__logo--collapsed' : ''}`}
        />
      </div> */}

      {/* Navigation */}
      <nav className="modern-sidebar__nav">
        {/* Main Menu Sections */}
        {menuItems.map((section) => (
          <div key={section.id} className="modern-sidebar__section">
            {/* Section Title */}
            {!effectivelyCollapsed && (
              <h3 className="modern-sidebar__section-title">
                {section.title}
              </h3>
            )}

            {/* Section Items */}
            <ul className="modern-sidebar__menu">
              {section.items.map((item, idx) => {
                const isActive = isActiveLink(item.link);

                return (
                  <li key={idx} className="modern-sidebar__menu-item">
                    <Link
                      to={item.link}
                      onClick={(e) => handleMenuItemClick(item, e)}
                      className={`modern-sidebar__menu-link ${effectivelyCollapsed ? 'modern-sidebar__menu-link--collapsed' : ''} ${isActive ? 'modern-sidebar__menu-link--active' : ''}`}
                      title={effectivelyCollapsed ? item.label : ""}
                    >
                      {/* Icon */}
                      <span className="modern-sidebar__menu-icon">
                        {item.icon}
                      </span>

                      {/* Label */}
                      {!effectivelyCollapsed && (
                        <span className="modern-sidebar__menu-label">
                          {item.label}
                        </span>
                      )}

                      {/* Tooltip for collapsed state */}
                      {effectivelyCollapsed && (
                        <span className="modern-sidebar__tooltip">
                          {item.label}
                        </span>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}

        {/* Bottom Menu */}
        <div className="modern-sidebar__bottom-menu">
          <ul className="modern-sidebar__menu">
            {bottomMenuItems.map((item, idx) => {
              const isActive = isActiveLink(item.link);

              return (
                <li key={idx} className="modern-sidebar__menu-item">
                  <Link
                    to={item.link}
                    onClick={(e) => handleMenuItemClick(item, e)}
                    className={`modern-sidebar__menu-link ${effectivelyCollapsed ? 'modern-sidebar__menu-link--collapsed' : ''} ${isActive ? 'modern-sidebar__menu-link--active' : ''}`}
                    title={effectivelyCollapsed ? item.label : ""}
                  >
                    {/* Icon */}
                    <span className="modern-sidebar__menu-icon">
                      {item.icon}
                    </span>

                    {/* Label */}
                    {!effectivelyCollapsed && (
                      <span className="modern-sidebar__menu-label">
                        {item.label}
                      </span>
                    )}

                    {/* Tooltip for collapsed state */}
                    {effectivelyCollapsed && (
                      <span className="modern-sidebar__tooltip">
                        {item.label}
                      </span>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      </nav>
    </aside>
  );
};

ModernSidebar.propTypes = {
  isCollapsed: PropTypes.bool.isRequired,
  isMobile: PropTypes.bool,
  isMobileMenuOpen: PropTypes.bool,
};

export default ModernSidebar;
